import { z } from "zod";
import { PublicationTrackingSchema, ClassificationSchema } from "./publication";

// ============================================================================
// SHARED SCHEMAS
// ============================================================================

export const FileItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  size: z.string(),
  type: z.string(),
  uploadDate: z.string(),
});

export const FAQItemSchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
});

export const InfoBlockSchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(),
});

export const EnrichmentDataSchema = z.object({
  files: z.array(FileItemSchema).nullable().optional(),
  faqs: z.array(FAQItemSchema).nullable().optional(),
  infoBlocks: z.array(InfoBlockSchema).nullable().optional(),
});
// ============================================================================
// BASE KNOWLEDGE BASE SCHEMA (matches backend DbKnowledgeBaseOutput)
// ============================================================================

// To sync with DbKnowledgeBaseOutput in backend/schemas/knowledge-base.ts
const BaseKnowledgeBaseSchema = z
  .object({
    // Primary fields
    id: z.string(),

    // Version releases
    kbGroupId: z.string(),
    releaseVersion: z.number().nullable(),
    status: z.string(), // 'draft' | 'released'
    releaseNote: z.string().nullable().optional(),

    // Relations
    workspaceId: z.string(),
    creatorId: z.string(),
    sourceKbId: z.string().nullable().optional(),
    publishedSourceKbId: z.string().nullable().optional(), // Harmonisé avec agents: published_source_*

    // History recording
    historyVersion: z.number(),
  })
  .merge(PublicationTrackingSchema)
  .merge(ClassificationSchema)
  .merge(EnrichmentDataSchema)
  .extend({
    // Timestamps
    createdAt: z.string(),
    updatedAt: z.string(),
    releasedAt: z.string().nullable().optional(),
    archivedAt: z.string().nullable().optional(),

    // Data fields
    name: z.string(),
    description: z.string().nullable().optional(),

    // Authorship
    initialAuthorName: z.string().nullable().optional(),
    initialAuthorId: z.string().nullable().optional(),

    // Retell integration
    retellKbId: z.string().nullable().optional(),

    createdWithAi: z.boolean().nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  });

export const KnowledgeBaseSchema = BaseKnowledgeBaseSchema;

export const KnowledgeBaseListItemSchema = BaseKnowledgeBaseSchema.extend({
  creatorFirstName: z.string().nullable().optional(),
  creatorLastName: z.string().nullable().optional(),
  creatorEmail: z.string().nullable().optional(),
  workspaceName: z.string().nullable().optional(),
  filesCount: z.number().nullable().optional(),
  faqsCount: z.number().nullable().optional(),
  infoBlocksCount: z.number().nullable().optional(),
});

export const KnowledgeBaseListSchema = z.array(KnowledgeBaseListItemSchema);

// ============================================================================
// CREATE/UPDATE PAYLOAD SCHEMAS (camelCase for API)
// ============================================================================

export const CreateKnowledgeBaseBodySchema = z.object({
  workspaceId: z.string().uuid("Invalid workspace ID"),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  specialty: z.string().optional(),
  usageContext: z.string().optional(),
  operations: z.array(z.string()).optional(),
  patientTypes: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  files: z.array(FileItemSchema).optional().default([]),
  faqs: z.array(FAQItemSchema).optional().default([]),
  infoBlocks: z.array(InfoBlockSchema).optional().default([]),
});

export const UpdateKnowledgeBaseBodySchema = z.object({
  historyVersion: z.number().int().positive(),
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  specialty: z.string().optional(),
  usageContext: z.string().optional(),
  operations: z.array(z.string()).optional(),
  patientTypes: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  files: z.array(FileItemSchema).optional(),
  faqs: z.array(FAQItemSchema).optional(),
  infoBlocks: z.array(InfoBlockSchema).optional(),
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type FileItem = z.infer<typeof FileItemSchema>;
export type FAQItem = z.infer<typeof FAQItemSchema>;
export type InfoBlock = z.infer<typeof InfoBlockSchema>;
export type EnrichmentData = z.infer<typeof EnrichmentDataSchema>;
export type CreateKnowledgeBaseBody = z.infer<typeof CreateKnowledgeBaseBodySchema>;
export type UpdateKnowledgeBaseBody = z.infer<typeof UpdateKnowledgeBaseBodySchema>;

// API Response types
export type CreateKnowledgeBaseApiResponse = KnowledgeBase;
export type UpdateKnowledgeBaseApiResponse = KnowledgeBase;
export type KnowledgeBaseListApiResponse = z.infer<typeof KnowledgeBaseListSchema>;

export type KnowledgeBase = z.infer<typeof KnowledgeBaseSchema>;
export type KnowledgeBaseListItem = z.infer<typeof KnowledgeBaseListItemSchema>;
